/**
 * Meta (Facebook) Pixel loader + per-event tracking API, gated on cookie consent.
 *
 * The pixel only loads when:
 *   1. REACT_APP_META_PIXEL_ID is set in the frontend env, AND
 *   2. The user has granted "marketing" consent via the cookie banner.
 *
 * If consent is later revoked we call `fbq('consent', 'revoke')` so the
 * already-injected script stops sending events. We never run a PageView or
 * a tracked conversion event until consent is granted.
 *
 * Conversion events fired via `track(name, params)` are queued internally
 * until the pixel is fully initialized (or until consent is granted). Once
 * ready, the queue flushes in order. If consent is denied, queued events
 * are discarded (we don't store them long-term).
 */
import { getConsent, CONSENT_EVENT } from './cookieConsent';

const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
let scriptInjected = false;
let initialized = false;
const eventQueue = [];

function injectPixelScript() {
  if (scriptInjected || !PIXEL_ID) return;
  // Standard Meta Pixel base code — assigns window.fbq and queues calls until
  // fbevents.js loads. We delay the actual init/PageView until consent is granted.
  /* eslint-disable */
  !function(f,b,e,v,n,t,s){
    if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)
  }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  // Default to revoked until explicit grant — Meta best practice.
  window.fbq && window.fbq('consent', 'revoke');
  scriptInjected = true;
}

function grantAndInit() {
  if (!window.fbq || !PIXEL_ID) return;
  window.fbq('consent', 'grant');
  if (!initialized) {
    window.fbq('init', PIXEL_ID);
    initialized = true;
  }
}

function revoke() {
  if (window.fbq) window.fbq('consent', 'revoke');
}

function flushQueue() {
  if (!initialized || !window.fbq) return;
  while (eventQueue.length) {
    const next = eventQueue.shift();
    try {
      window.fbq('track', next.name, next.params || {});
    } catch (_) {
      /* swallow — never block app on a pixel failure */
    }
  }
}

function applyConsent() {
  if (!PIXEL_ID) return; // pixel disabled in this environment
  const c = getConsent();
  if (c?.marketing) {
    injectPixelScript();
    setTimeout(() => {
      grantAndInit();
      // PageView is the implicit first event after init.
      if (window.fbq) window.fbq('track', 'PageView');
      flushQueue();
    }, 0);
  } else if (scriptInjected) {
    revoke();
    // Drop any queued events so they don't leak if consent flips back later
    // for a different intent — we want each session's events to be fresh.
    eventQueue.length = 0;
  }
}

/**
 * Wire the pixel to the consent store. Safe to call once at app boot —
 * idempotent and a no-op if REACT_APP_META_PIXEL_ID isn't set.
 */
export function initMetaPixelGate() {
  if (!PIXEL_ID) return;
  applyConsent();
  window.addEventListener(CONSENT_EVENT, applyConsent);
}

/**
 * Fire a Meta Pixel standard event (e.g. 'Lead', 'Subscribe', 'Purchase').
 *
 *   track('Subscribe', { value: 79, currency: 'USD', content_name: 'Pro' })
 *
 * Safe to call regardless of consent state — when consent or env-var aren't
 * granted, the call is a silent no-op. When the pixel script is still
 * loading, the event is queued and flushed once initialization completes.
 */
export function track(eventName, params = {}) {
  if (!PIXEL_ID || !eventName) return;
  const consent = getConsent();
  if (!consent?.marketing) return; // gated
  eventQueue.push({ name: eventName, params });
  if (!scriptInjected) {
    // Should never happen if applyConsent ran, but defensively load.
    injectPixelScript();
    setTimeout(() => { grantAndInit(); flushQueue(); }, 0);
    return;
  }
  // If fbevents.js hasn't finished loading yet, fbq queues internally too —
  // calling fbq directly still works because the snippet's stub buffers.
  if (initialized && window.fbq) {
    flushQueue();
  } else {
    // Wait a tick for init then flush.
    setTimeout(() => { grantAndInit(); flushQueue(); }, 0);
  }
}

// Expose on window for easy diagnostic console use and external testing.
if (typeof window !== 'undefined') {
  window.driveHRTrack = track;
}
